// Touch support for old single-touch POS machines.
//
// 1. Touch mode: hides the mouse cursor. Auto-detected from touch capability,
//    can be forced per machine with localStorage POS_TOUCH_MODE = "Y" / "N".
// 2. Drag to scroll: these screens emulate a mouse, so native overflow
//    containers (dropdown menus, multiselect, modals, lists) never scroll on a
//    swipe. Dragging now scrolls the nearest scrollable container. Containers
//    using v-bscroll / v-dragscroll handle themselves and are skipped.

const TOUCH_MODE_KEY = "POS_TOUCH_MODE";
const TOUCH_MODE_CLASS = "pos-touch-mode";
const DRAG_THRESHOLD = 6;
const SCROLLABLE_OVERFLOW = /(auto|scroll|overlay)/;
const IGNORE_TAGS = /^(INPUT|TEXTAREA|SELECT|OPTION|VIDEO|CANVAS)$/;

const detectTouchMode = () => {
  const override = window.localStorage.getItem(TOUCH_MODE_KEY);
  if (override === "Y") return true;
  if (override === "N") return false;
  return (
    navigator.maxTouchPoints > 0 ||
    "ontouchstart" in window ||
    (window.matchMedia && window.matchMedia("(any-pointer: coarse)").matches)
  );
};

const setTouchMode = (enabled) => {
  document.documentElement.classList.toggle(TOUCH_MODE_CLASS, enabled);
};

const initTouchMode = () => {
  setTouchMode(detectTouchMode());
  // Detection can miss some drivers; the first real touch switches it on.
  window.addEventListener(
    "touchstart",
    () => {
      if (window.localStorage.getItem(TOUCH_MODE_KEY) !== "N") {
        setTouchMode(true);
      }
    },
    { once: true, passive: true }
  );
};

const findScrollable = (target) => {
  let el = target;
  while (el && el !== document.body && el !== document.documentElement) {
    if (el.nodeType === 1) {
      if (el._betterScroll || el._dragScrollDown) return null;
      if (el.isContentEditable || el.hasAttribute("data-no-dragscroll")) {
        return null;
      }
      const style = window.getComputedStyle(el);
      const canY =
        SCROLLABLE_OVERFLOW.test(style.overflowY) &&
        el.scrollHeight > el.clientHeight + 1;
      const canX =
        SCROLLABLE_OVERFLOW.test(style.overflowX) &&
        el.scrollWidth > el.clientWidth + 1;
      if (canX || canY) return { el, canX, canY };
    }
    el = el.parentElement;
  }
  return null;
};

const initDragScroll = () => {
  let state = null;
  let suppressClick = false;

  const onMove = (e) => {
    if (!state) return;
    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;
    if (
      !state.moved &&
      Math.abs(dx) < DRAG_THRESHOLD &&
      Math.abs(dy) < DRAG_THRESHOLD
    ) {
      return;
    }
    if (!state.moved) {
      state.moved = true;
      const selection = window.getSelection && window.getSelection();
      if (selection) selection.removeAllRanges();
    }
    e.preventDefault();
    if (state.canX) state.el.scrollLeft = state.startScrollLeft - dx;
    if (state.canY) state.el.scrollTop = state.startScrollTop - dy;
  };

  const onUp = () => {
    window.removeEventListener("mousemove", onMove, true);
    window.removeEventListener("mouseup", onUp, true);
    if (state && state.moved) {
      // The click fired right after this mouseup must not hit a button.
      suppressClick = true;
      setTimeout(() => {
        suppressClick = false;
      }, 50);
    }
    state = null;
  };

  const onDown = (e) => {
    if (e.button !== 0 || state) return;
    const target = e.target;
    if (!target || IGNORE_TAGS.test(target.tagName)) return;
    const scrollable = findScrollable(target);
    if (!scrollable) return;
    state = {
      ...scrollable,
      startX: e.clientX,
      startY: e.clientY,
      startScrollLeft: scrollable.el.scrollLeft,
      startScrollTop: scrollable.el.scrollTop,
      moved: false,
    };
    window.addEventListener("mousemove", onMove, true);
    window.addEventListener("mouseup", onUp, true);
  };

  const onClick = (e) => {
    if (!suppressClick) return;
    suppressClick = false;
    e.preventDefault();
    e.stopPropagation();
  };

  // Images and links would otherwise start a native drag and stop mousemove.
  const onDragStart = (e) => {
    if (state) e.preventDefault();
  };

  window.addEventListener("mousedown", onDown, true);
  window.addEventListener("click", onClick, true);
  window.addEventListener("dragstart", onDragStart, true);
};

export const TouchSupportPlugin = {
  install() {
    if (typeof window === "undefined") return;
    initTouchMode();
    initDragScroll();
  },
};



// WEBPACK FOOTER //
// ./src/plugins/touchSupport.js